import React, { useState, useEffect } from 'react';
import { Plus, Users, UserPlus, UserMinus, Trash2, Edit2, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../hooks/useToast';
import { Team, TeamMember, TeamCreate } from '../types/Team';
import api from '../services/api';

export default function Teams() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [formData, setFormData] = useState<TeamCreate>({
    name: '',
    company: 0,
    leader: null,
    description: '',
    member_ids: []
  });
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Buscar equipes
  const { data: teams, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await api.get<{ results: Team[] }>('/auth/teams/');
      return response.data.results || response.data;
    }
  });

  // Buscar usuários
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/auth/users/');
      return response.data.results || response.data;
    }
  });

  // Criar/Atualizar equipe
  const teamMutation = useMutation({
    mutationFn: async (data: TeamCreate) => {
      if (editingTeam) {
        return await api.patch(`/auth/teams/${editingTeam.id}/`, data);
      }
      return await api.post('/auth/teams/', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsModalOpen(false);
      setEditingTeam(null);
      setFormData({
        name: '',
        company: 0,
        leader: null,
        description: '',
        member_ids: []
      });
      showToast(editingTeam ? 'Equipe atualizada com sucesso!' : 'Equipe criada com sucesso!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao salvar equipe', 'error');
    }
  });

  // Excluir equipe
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await api.delete(`/auth/teams/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showToast('Equipe excluída com sucesso!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao excluir equipe', 'error');
    }
  });

  // Adicionar/Remover membro
  const memberMutation = useMutation({
    mutationFn: async ({ teamId, userId, action }: { teamId: number; userId: number; action: 'add' | 'remove' }) => {
      const endpoint = action === 'add' ? 'add-member' : 'remove-member';
      return await api.post(`/auth/teams/${teamId}/${endpoint}/`, { user_id: userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      showToast('Membros atualizados com sucesso!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao atualizar membros', 'error');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Obter company_id do localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      showToast('Usuário não autenticado', 'error');
      return;
    }
    
    const user = JSON.parse(userStr);
    const teamData = {
      ...formData,
      company: user.company?.id || 0
    };
    
    teamMutation.mutate(teamData);
  };

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      company: team.company,
      leader: team.leader,
      description: team.description || '',
      member_ids: team.members.map((m: TeamMember) => m.user)
    });
    setIsModalOpen(true);
  };

  const handleDelete = (teamId: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta equipe?')) {
      deleteMutation.mutate(teamId);
    }
  };

  const toggleMember = (userId: number) => {
    const memberIds = formData.member_ids || [];
    if (memberIds.includes(userId)) {
      setFormData({
        ...formData,
        member_ids: memberIds.filter(id => id !== userId)
      });
    } else {
      setFormData({
        ...formData,
        member_ids: [...memberIds, userId]
      });
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid var(--glass-border)',
          borderTopColor: 'var(--color-primary-600)',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>Equipes</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>Gerencie as equipes da sua empresa</p>
        </div>
        <button
          onClick={() => {
            setEditingTeam(null);
            setFormData({
              name: '',
              company: 0,
              leader: null,
              description: '',
              member_ids: []
            });
            setIsModalOpen(true);
          }}
          style={{
            background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
            color: 'white',
            padding: 'var(--space-2) var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontWeight: '500',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          <Plus className="w-4 h-4" />
          Nova Equipe
        </button>
      </div>

      {/* Lista de Equipes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-6)' }}>
        {teams?.map((team) => (
          <div key={team.id} style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-6)', boxShadow: 'var(--shadow-glass)' }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{team.name}</h3>
                  <p className="text-sm text-gray-500">{team.member_count} membros</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(team)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Editar"
                >
                  <Edit2 className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={() => handleDelete(team.id)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                  title="Excluir"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>

            {team.description && (
              <p className="text-gray-600 text-sm mb-4">{team.description}</p>
            )}

            {team.leader_name && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">Líder</p>
                <p className="text-sm font-medium text-gray-900">{team.leader_name}</p>
              </div>
            )}

            {/* Membros */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500">Membros</p>
                <button
                  onClick={() => handleEdit(team)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Gerenciar
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {team.members.slice(0, 3).map((member: TeamMember) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-2 bg-gray-50 px-2 py-1 rounded"
                  >
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs">
                      {member.user_name.charAt(0)}
                    </div>
                    <span className="text-xs text-gray-700">{member.user_name}</span>
                  </div>
                ))}
                {team.members.length > 3 && (
                  <div className="text-xs text-gray-500 px-2 py-1">
                    +{team.members.length - 3} outros
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Criar/Editar Equipe */}
      {isModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-2xl)', border: '1px solid var(--glass-border)', boxShadow: 'var(--shadow-xl)', maxWidth: '672px', width: '100%', margin: '0 var(--space-4)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingTeam ? 'Editar Equipe' : 'Nova Equipe'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Equipe *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Equipe Enterprise"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Descreva o objetivo desta equipe..."
                />
              </div>

              {/* Líder */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Líder da Equipe
                </label>
                <select
                  value={formData.leader || ''}
                  onChange={(e) => setFormData({ ...formData, leader: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione um líder...</option>
                  {users?.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              {/* Membros */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Membros da Equipe
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {users?.map((user: any) => {
                    const isSelected = (formData.member_ids || []).includes(user.id);
                    return (
                      <label
                        key={user.id}
                        className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleMember(user.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                          {user.role}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={teamMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {teamMutation.isPending ? 'Salvando...' : editingTeam ? 'Atualizar' : 'Criar Equipe'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}